import { createContext, ReactNode, useContext } from "react";

export type ThemeMode = "both" | "light" | "dark";

export const GalleryThemeContext = createContext<ThemeMode>("both");

/** Stand-in content for containers; galleries show components, not data. */
export const Placeholder = ({ label = "content", className = "" }: { label?: string; className?: string }) => (
    <div className={`gallery-placeholder ${className}`}>{label}</div>
);

/** Box that contains fixed/absolute components (modal, navbar, popover) so they render in place. */
export const Stage = ({ height, children }: { height: string; children: ReactNode }) => (
    <div className="gallery-stage" style={{ height }}>{children}</div>
);

const ClassTag = ({ name }: { name: string }) => <code className="code-chip">.{name}</code>;

/**
 * One documented component: name, its classes, when to use it, the rules that keep it
 * consistent, a live demo rendered in each theme, and the call-site code.
 * `demo` is a render function so each theme gets its own component state.
 */
export const Specimen = ({
    id,
    name,
    classes,
    usage,
    rules,
    code,
    demo,
}: {
    id?: string;
    name: string;
    classes: string[];
    usage: ReactNode;
    rules?: ReactNode[];
    code?: string;
    demo: () => ReactNode;
}) => {
    const mode = useContext(GalleryThemeContext);
    const themes = mode === "both" ? (["light", "dark"] as const) : [mode];

    return (
        <article id={id} className="specimen">
            <header className="specimen-header">
                <div className="specimen-title">
                    <p className="hsub">{name}</p>
                    <div className="specimen-classes">
                        {classes.map((c) => <ClassTag key={c} name={c} />)}
                    </div>
                </div>
                <p className="specimen-usage">{usage}</p>
                {rules && rules.length > 0 && (
                    <ul className="specimen-rules">
                        {rules.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                )}
            </header>
            <div className={`specimen-stages ${themes.length === 1 ? "specimen-stages-single" : ""}`}>
                {themes.map((t) => (
                    <div key={t} data-theme={t} className="specimen-theme">
                        <span className="specimen-theme-label">{t}</span>
                        {demo()}
                    </div>
                ))}
            </div>
            {code && <pre className="specimen-code">{code.trim()}</pre>}
        </article>
    );
};

export const GallerySection = ({ id, title, intro, children }: { id: string; title: string; intro: ReactNode; children: ReactNode }) => (
    <section id={id} className="gallery-section">
        <div className="gallery-section-header">
            <p className="eyebrow">Section</p>
            <p className="h2">{title}</p>
            <p className="b text-black/70">{intro}</p>
        </div>
        {children}
    </section>
);

export const noop = () => { };
